import api from './apiService';
import pageSpeedInsightsService from './PageSpeedInsightsService';
import websiteService from './websiteService';

/**
 * Service for handling PageSpeed analysis of websites
 */
class WebsitePageSpeedService {
  /**
   * Run PageSpeed analysis on a website and save results to backend
   * @param {Object} website - Website object with root_url and id
   * @param {Object} options - Options for PageSpeed analysis
   * @param {boolean} [options.mobile=false] - Whether to run mobile analysis
   * @param {boolean} [options.desktop=true] - Whether to run desktop analysis
   * @param {Function} [options.onProgress] - Callback for progress updates
   * @returns {Promise<Object>} - Object containing analysis results
   */
  async analyzeWebsite(website, options = {}) {
    if (!website || !website.root_url) {
      throw new Error('Website URL is required for PageSpeed analysis');
    }

    // Default options
    const {
      mobile = false,
      desktop = true,
      onProgress = () => {}
    } = options;

    try {
      const results = {
        websiteId: website.id,
        url: website.root_url,
        desktop: null,
        mobile: null
      };

      // Update progress
      onProgress({
        status: 'starting',
        message: 'Initializing PageSpeed analysis...',
        progress: 0
      });

      // Run desktop analysis if requested
      if (desktop) {
        onProgress({
          status: 'running',
          message: 'Running desktop analysis...',
          progress: 10
        });

        const desktopResult = await this._runAndSaveAnalysis(website, { mobile: false });
        results.desktop = desktopResult;

        onProgress({
          status: 'running',
          message: 'Desktop analysis complete',
          progress: 50
        });
      }

      // Run mobile analysis if requested
      if (mobile) {
        onProgress({
          status: 'running',
          message: 'Running mobile analysis...',
          progress: desktop ? 60 : 10
        });

        const mobileResult = await this._runAndSaveAnalysis(website, { mobile: true });
        results.mobile = mobileResult;

        onProgress({
          status: 'running',
          message: 'Mobile analysis complete',
          progress: 90
        });
      }

      // Final progress update
      onProgress({
        status: 'complete',
        message: 'PageSpeed analysis complete!',
        progress: 100
      });

      return results;
    } catch (error) {
      console.error('Error analyzing website with PageSpeed:', error);
      
      // Update progress with error
      onProgress({
        status: 'error',
        message: error.message || 'Error running PageSpeed analysis',
        progress: 0
      });
      
      throw error;
    }
  }

  /**
   * Run PageSpeed analysis on a specific page and save results to backend
   * @param {Object} page - Page object with url, id, and website_id
   * @param {Object} options - Options for PageSpeed analysis
   * @param {boolean} [options.mobile=false] - Whether to run mobile analysis
   * @param {boolean} [options.desktop=true] - Whether to run desktop analysis
   * @param {Function} [options.onProgress] - Callback for progress updates
   * @returns {Promise<Object>} - Object containing analysis results
   */
  async analyzePage(page, options = {}) {
    if (!page || !page.url) {
      throw new Error('Page URL is required for PageSpeed analysis');
    }

    if (!page.id) {
      throw new Error('Page ID is required for PageSpeed analysis');
    }

    if (!page.website_id && !page.website) {
      throw new Error('Website ID is required for PageSpeed analysis');
    }

    // Default options
    const {
      mobile = true,
      desktop = true,
      onProgress = () => {}
    } = options;

    try {
      const results = {
        pageId: page.id,
        websiteId: page.website_id,
        url: page.url,
        desktop: null,
        mobile: null
      };

      // Update progress
      onProgress({
        status: 'starting',
        message: `Initializing PageSpeed analysis for page: ${page.url}...`,
        progress: 0
      });

      // Run desktop analysis if requested
      if (desktop) {
        onProgress({
          status: 'running',
          message: `Analyzing desktop performance for ${page.url}...`,
          progress: 25
        });

        results.desktop = await this._runAndSavePageAnalysis(page, { mobile: false });
        
        onProgress({
          status: 'progress',
          message: 'Desktop analysis complete. Processing mobile...',
          progress: 50
        });
      }

      // Run mobile analysis if requested
      if (mobile) {
        onProgress({
          status: 'running',
          message: `Analyzing mobile performance for ${page.url}...`,
          progress: 75
        });

        results.mobile = await this._runAndSavePageAnalysis(page, { mobile: true });
        
        onProgress({
          status: 'progress',
          message: 'Mobile analysis complete.',
          progress: 90
        });
      }

      // Final progress update
      onProgress({
        status: 'complete',
        message: `PageSpeed analysis complete for ${page.url}!`,
        progress: 100
      });

      return results;
    } catch (error) {
      console.error('Error analyzing page with PageSpeed:', error);
      
      // Update progress with error
      onProgress({
        status: 'error',
        message: error.message || 'Error running PageSpeed analysis',
        progress: 0
      });
      
      throw error;
    }
  }

  /**
   * Run PageSpeed analysis and save results to backend for a specific page
   * @private
   * @param {Object} page - Page object with URL, ID and website_id
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - PageSpeed results
   */
  async _runAndSavePageAnalysis(page, options = {}) {
    try {
      // Extract URL from page object
      const url = page.url;
      const { mobile = false } = options;
      
      // Run PageSpeed analysis using PageSpeedInsightsService
      const psiData = await pageSpeedInsightsService.analyzeUrl(url, {
        mobile,
        strategy: mobile ? 'mobile' : 'desktop',
        category: 'performance,accessibility,best-practices,seo,pwa'
      });
      
      // Transform the results to our format
      const { metrics, response } = pageSpeedInsightsService.transformResults(psiData, url, { mobile });
      
      // Save results to our backend with page_id
      await this._savePageSpeedResults(page.website_id, url, {
        is_mobile: mobile,
        performance_score: response.metrics.performance.score,
        raw_metrics: JSON.stringify(metrics),
        raw_data: JSON.stringify(response.rawData),
        page_id: page.id // Include page ID for individual page analysis
      });
      
      return response;
    } catch (error) {
      console.error(`Error in _runAndSavePageAnalysis for ${page.url}:`, error);
      throw error;
    }
  }

  /**
   * Run PageSpeed analysis and save results to backend
   * @private
   * @param {Object} website - Website object
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - PageSpeed results
   */
  async _runAndSaveAnalysis(website, options = {}) {
    try {
      // Extract URL from website object
      const url = website.root_url;
      const { mobile = false } = options;
      
      // Run PageSpeed analysis using PageSpeedInsightsService
      const psiData = await pageSpeedInsightsService.analyzeUrl(url, {
        mobile,
        strategy: mobile ? 'mobile' : 'desktop',
        category: 'performance,accessibility,best-practices,seo,pwa'
      });
      
      // Transform the results to our format
      const { metrics, response } = pageSpeedInsightsService.transformResults(psiData, url, { mobile });
      
      // Save results to our backend
      await this._savePageSpeedResults(website.id, url, {
        is_mobile: mobile,
        performance_score: response.metrics.performance.score,
        raw_metrics: JSON.stringify(metrics),
        raw_data: JSON.stringify(response.rawData)
      });
      
      return response;
    } catch (error) {
      console.error(`Error in _runAndSaveAnalysis for ${website.root_url}:`, error);
      throw error;
    }
  }

  /**
   * Save PageSpeed results to backend
   * @private
   * @param {number} websiteId - Website ID
   * @param {string} url - Website URL
   * @param {Object} data - PageSpeed data to save
   * @returns {Promise<Object>} - Saved PageSpeed data
   */
  async _savePageSpeedResults(websiteId, url, data) {
    try {
      const payload = {
        url,
        website_id: websiteId,
        is_mobile: data.is_mobile || false,
        browser: 'chrome',
        performance_score: data.performance_score || 0,
        raw_metrics: data.raw_metrics,
        raw_data: data.raw_data
      };
      
      // Include page_id if available for individual page analysis
      if (data.page_id) {
        payload.page_id = data.page_id;
      }
      
      const response = await api.post('/user-page-speeds', payload);
      return response.data;
    } catch (error) {
      console.error('Error saving PageSpeed results:', error);
      throw error;
    }
  }
  
  /**
   * Get PageSpeed analysis for a website
   * @param {number} websiteId - Website ID
   * @param {Object} options - Options for fetching analysis
   * @param {boolean} [options.mobile] - If set, filter by mobile/desktop
   * @returns {Promise<Object>} - PageSpeed analysis data
   */
  async getWebsitePageSpeed(websiteId, options = {}) {
    try {
      const response = await api.get(`/websites/${websiteId}/page-speeds`, {
        params: options
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting PageSpeed for website ID ${websiteId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get latest PageSpeed analysis for a URL
   * @param {string} url - Website URL
   * @param {Object} options - Options for fetching analysis
   * @param {boolean} [options.is_mobile] - Whether to get mobile results
   * @returns {Promise<Object>} - Latest PageSpeed analysis
   */
  async getLatestPageSpeedByUrl(url, options = {}) {
    try {
      const response = await api.get('/user-page-speeds/latest', {
        params: {
          url,
          ...options
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting latest PageSpeed for ${url}:`, error);
      throw error;
    }
  }
  
  /**
   * Get PageSpeed history for a URL
   * @param {string} url - Website URL
   * @param {Object} options - Options for fetching history
   * @param {boolean} [options.is_mobile] - Whether to get mobile results
   * @param {number} [options.limit=10] - Number of history entries to fetch
   * @returns {Promise<Array>} - PageSpeed history
   */
  async getPageSpeedHistory(url, options = {}) {
    try {
      const response = await api.get('/user-page-speeds/history', {
        params: {
          url,
          limit: options.limit || 10,
          ...options
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting PageSpeed history for ${url}:`, error);
      throw error;
    }
  }

  /**
   * Get PageSpeed data for a website by website ID
   * @param {number} websiteId - Website ID
   * @param {Object} options - Options for fetching analysis
   * @param {boolean} [options.is_mobile] - Whether to get mobile results
   * @param {number} [options.limit=1] - Number of results to fetch
   * @param {number} [options.offset=0] - Offset for pagination
   * @returns {Promise<Object>} - PageSpeed analysis data with parsed metrics
   */
  async getWebsitePageSpeedById(websiteId, options = {}) {
    try {
      // Validate websiteId input
      if (!websiteId) {
        console.error("Cannot fetch PageSpeed data: Missing website ID");
        return { success: false, message: "Missing website ID", analyses: [] };
      }

      // Determine the URL from website object if available, otherwise use root URL
      const response = await websiteService.getWebsiteById(websiteId);
      
      // Check if website exists
      if (!response) {
        console.error(`Cannot fetch PageSpeed data: Website with ID ${websiteId} not found`);
        return { success: false, message: "Website not found", analyses: [] };
      }
      
      // Extract the website from the response data structure
      const website = response.website || response;
      
      // If we have a website object but it has no URL properties, log and return gracefully
      if (!website || (!website.root_url && !website.url)) {
        console.error(`Cannot fetch PageSpeed data: Missing URL for website ID ${websiteId}`, website);
        return { 
          success: false, 
          message: "Missing website URL", 
          websiteId: websiteId,
          analyses: [] 
        };
      }
      
      // Get the URL, trying several possible property names
      const url = website.root_url || website.url || website.website_url || website.domain;

      // Double-check we have a URL
      if (!url) {
        console.error(`Cannot fetch PageSpeed data: Missing URL for website ID ${websiteId}`, website);
        return { 
          success: false, 
          message: "Missing website URL", 
          websiteId: websiteId,
          analyses: [] 
        };
      }

      console.log(`Fetching PageSpeed data for website ID ${websiteId} with URL ${url}`);
      
      // Use the endpoint with URL and website_id parameters
      const pageSpeedResponse = await api.get('/user-page-speeds/url', {
        params: {
          url: url,
          website_id: websiteId,
          is_mobile: options.is_mobile,
          limit: options.limit || 1
        }
      });
      
      // Process the response to parse raw_metrics and enhance with calculated values
      if (pageSpeedResponse.data.success && 
          pageSpeedResponse.data.analyses && 
          pageSpeedResponse.data.analyses.length > 0) {
        // Parse the raw_metrics JSON string in each analysis
        pageSpeedResponse.data.analyses = pageSpeedResponse.data.analyses.map(analysis => {
          // Create a new object with all original properties
          const enhancedAnalysis = { ...analysis };
          
          // Parse raw_metrics if it exists and is a string
          if (analysis.raw_metrics) {
            try {
              // First, ensure we're working with a string (it could already be an object)
              const rawMetricsStr = typeof analysis.raw_metrics === 'string' 
                ? analysis.raw_metrics 
                : JSON.stringify(analysis.raw_metrics);
                
              enhancedAnalysis.parsedMetrics = JSON.parse(rawMetricsStr);
              
              // Check if we have nested values in raw_metrics that need additional parsing
              if (enhancedAnalysis.parsedMetrics && typeof enhancedAnalysis.parsedMetrics === 'string') {
                try {
                  enhancedAnalysis.parsedMetrics = JSON.parse(enhancedAnalysis.parsedMetrics);
                } catch (nestedError) {
                  console.warn('Could not parse nested raw_metrics JSON, using as is');
                }
              }
              
              // Apply the parsed values to the top-level properties if they're currently 0
              if (enhancedAnalysis.load_time === 0 && enhancedAnalysis.parsedMetrics.load_time) {
                enhancedAnalysis.load_time = enhancedAnalysis.parsedMetrics.load_time;
              }
              if (enhancedAnalysis.page_load_time === 0 && enhancedAnalysis.parsedMetrics.page_load_time) {
                enhancedAnalysis.page_load_time = enhancedAnalysis.parsedMetrics.page_load_time;
              }
              if (enhancedAnalysis.dom_complete === 0 && enhancedAnalysis.parsedMetrics.dom_complete) {
                enhancedAnalysis.dom_complete = enhancedAnalysis.parsedMetrics.dom_complete;
              }
              if (enhancedAnalysis.first_paint === 0 && enhancedAnalysis.parsedMetrics.first_paint) {
                enhancedAnalysis.first_paint = enhancedAnalysis.parsedMetrics.first_paint;
              }
              if (enhancedAnalysis.transfer_size === 0 && enhancedAnalysis.parsedMetrics.transfer_size) {
                enhancedAnalysis.transfer_size = enhancedAnalysis.parsedMetrics.transfer_size;
              }
              if (enhancedAnalysis.requests === 0 && enhancedAnalysis.parsedMetrics.requests) {
                enhancedAnalysis.requests = enhancedAnalysis.parsedMetrics.requests;
              }
              if (enhancedAnalysis.cpu_load_percent === 0 && enhancedAnalysis.parsedMetrics.cpu_load_percent) {
                enhancedAnalysis.cpu_load_percent = enhancedAnalysis.parsedMetrics.cpu_load_percent;
              }
              if (enhancedAnalysis.memory_usage_mb === 0 && enhancedAnalysis.parsedMetrics.memory_usage_mb) {
                enhancedAnalysis.memory_usage_mb = enhancedAnalysis.parsedMetrics.memory_usage_mb;
              }
              
              // Add derived metrics
              enhancedAnalysis.formattedDate = new Date(analysis.analyzed_at).toLocaleDateString();
              enhancedAnalysis.formattedLoadTime = (enhancedAnalysis.load_time / 1000).toFixed(2) + 's';
              enhancedAnalysis.formattedFirstPaint = (enhancedAnalysis.first_paint / 1000).toFixed(2) + 's';
              enhancedAnalysis.formattedTransferSize = (enhancedAnalysis.transfer_size / (1024 * 1024)).toFixed(2) + ' MB';
            } catch (e) {
              console.error('Error parsing raw_metrics:', e);
              enhancedAnalysis.parsedMetrics = {};
            }
          }
          
          // Also parse raw_data if it exists
          if (analysis.raw_data && typeof analysis.raw_data === 'string') {
            try {
              enhancedAnalysis.parsedRawData = JSON.parse(analysis.raw_data);
            } catch (e) {
              console.error('Error parsing raw_data:', e);
              enhancedAnalysis.parsedRawData = {};
            }
          }
          
          // Add device type indicator for easier frontend handling
          enhancedAnalysis.deviceType = analysis.is_mobile ? 'mobile' : 'desktop';
          
          return enhancedAnalysis;
        });
      }
      
      return pageSpeedResponse.data;
    } catch (error) {
      console.error(`Error getting PageSpeed for website ID ${websiteId}:`, error);
      // Return a structured error response instead of throwing
      return { 
        success: false, 
        message: error.message || "Error fetching PageSpeed data", 
        websiteId: websiteId,
        analyses: [] 
      };
    }
  }

  /**
   * Wait for crawling to complete
   * @param {string} websiteId - The website ID to check
   * @param {Object} options - Options
   * @returns {Promise<Object>} The completed website
   */
  async waitForCrawlingToComplete(websiteId, options = {}) {
    if (!websiteId) {
      throw new Error('Website ID is required');
    }
    
    const { 
      maxAttempts = 20, 
      delayMs = 3000,
      timeoutMs = 60000
    } = options;
    
    // Start timeout counter
    const startTime = Date.now();
    
    // Initialize attempt counter
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        // Check if we've exceeded the timeout
        if (Date.now() - startTime > timeoutMs) {
          return reject(new Error(`Timeout waiting for website crawling to complete after ${timeoutMs}ms`));
        }
        
        // Check if we've exceeded max attempts
        if (attempts >= maxAttempts) {
          return reject(new Error(`Exceeded maximum attempts (${maxAttempts}) waiting for website crawling`));
        }
        
        try {
          // Increment attempt counter
          attempts++;
          
          // Fetch the website
          const response = await websiteService.getWebsiteById(websiteId);
          
          // Get the website data
          const website = response.website || response;
          
          // Check if crawling is complete
          if (website.status === 'completed' || website.crawling_status === 'completed') {
            // Crawling is complete, resolve with the website
            return resolve(website);
          } else if (website.status === 'error' || website.crawling_status === 'error') {
            // Crawling failed, reject with an error
            return reject(new Error(`Website crawling failed with status: ${website.status || website.crawling_status}`));
          } else {
            // Crawling is still in progress, wait and try again
            console.log(`Website crawling in progress (${website.status || website.crawling_status}), attempt ${attempts}/${maxAttempts}`);
            setTimeout(checkStatus, delayMs);
          }
        } catch (error) {
          console.error(`Error checking website status:`, error);
          // If there was an error fetching the website, wait and try again
          setTimeout(checkStatus, delayMs);
        }
      };
      
      // Start checking status
      checkStatus();
    });
  }

  /**
   * Get PageSpeed data by URL and website ID
   * @param {string} url - The page URL
   * @param {string} websiteId - The website ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Response with PageSpeed data
   */
  async getPageSpeedByUrlAndWebsiteId(url, websiteId, options = {}) {
    if (!url) {
      throw new Error('URL is required');
    }

    if (!websiteId) {
      throw new Error('Website ID is required');
    }

    try {
      const queryParams = new URLSearchParams();
      
      // Add required params
      queryParams.append('url', url);
      queryParams.append('website_id', websiteId);
      
      // Add optional params
      if (options.is_mobile !== undefined) {
        queryParams.append('is_mobile', options.is_mobile);
      }
      if (options.limit !== undefined) {
        queryParams.append('limit', options.limit);
      }

      console.log(`Fetching PageSpeed data for URL: ${url}, website ID: ${websiteId}`);
      const response = await api.get(`/user-page-speeds/url?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching PageSpeed data by URL and website ID:', error);
      throw error;
    }
  }
}

// Create singleton instance
const websitePageSpeedService = new WebsitePageSpeedService();

export default websitePageSpeedService;